// src/context/AuthContextFixed.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, rtdb } from "../firebase";
import { ref, onValue, get } from "firebase/database";

// Crée le Contexte d'authentification
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext);
};

// Composant Fournisseur d'authentification
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userGreenhouses, setUserGreenhouses] = useState([]);
  const [hasPushToken, setHasPushToken] = useState(false);
  const [selectedGreenhouse, setSelectedGreenhouse] = useState(() => {
    // Récupérer la serre sélectionnée depuis localStorage au démarrage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('bioconnect_selected_greenhouse');
      return saved || null;
    }
    return null;
  });

  // Charger les serres accessibles à l'utilisateur
  const loadUserGreenhouses = (userId) => {
    const membershipRef = ref(rtdb, `memberships/${userId}`);
    
    const unsubscribe = onValue(membershipRef, (snapshot) => {
      if (snapshot.exists()) {
        const memberships = snapshot.val();
        
        const greenhouseIds = Object.keys(memberships).filter(
          greenhouseId => memberships[greenhouseId] === true
        );
        
        setUserGreenhouses(greenhouseIds);
        
        // Sélectionner la serre sauvegardée ou la première par défaut
        if (greenhouseIds.length > 0 && !selectedGreenhouse) {
          const savedGreenhouse = localStorage.getItem('bioconnect_selected_greenhouse');
          const validSaved = savedGreenhouse && greenhouseIds.includes(savedGreenhouse);
          
          if (validSaved) {
            setSelectedGreenhouse(savedGreenhouse);
          } else {
            const firstGreenhouse = greenhouseIds[0];
            setSelectedGreenhouse(firstGreenhouse);
            localStorage.setItem('bioconnect_selected_greenhouse', firstGreenhouse);
          }
        }
      } else {
        setUserGreenhouses([]);
      }
    }, (error) => {
      console.error("Error loading user greenhouses:", error);
    });
    
    return unsubscribe;
  };

  useEffect(() => {
    // Écoute les changements d'état d'authentification Firebase
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Charger les serres accessibles à l'utilisateur
        loadUserGreenhouses(user.uid);
      } else {
        setUserGreenhouses([]);
        setSelectedGreenhouse(null);
      }
      
      setLoading(false);
    });

    // Nettoyage de l'écouteur lors du démontage du composant
    return () => {
      unsubscribeAuth();
    };
  }, []);

  // Vérifier si pushTokens/[uid]/ contient déjà des tokens lors d'une connexion
  // Reset pushToken dès que l'utilisateur se déconnecte
  useEffect(() => {
    if (!currentUser) {
      setTimeout(() => setHasPushToken(false), 0);
    }
  }, [currentUser]);

  // Vérifie l'existence du pushToken quand currentUser présent
  useEffect(() => {
    if (!currentUser) return;
    let active = true;
    get(ref(rtdb, `pushTokens/${currentUser.uid}`))
      .then((snapshot) => {
        if (active) setHasPushToken(snapshot.exists() && !!snapshot.val());
      })
      .catch(() => {
        if (active) setHasPushToken(false);
      });
    return () => {
      active = false;
    };
  }, [currentUser]);

  // Méthode pour MAJ le state local après ajout token (utilisé côté App)
  const setPushTokenPresent = (val = true) => setHasPushToken(val);

  // Fonctions d'authentification
  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    
    // Mise à jour locale de l'état après inscription
    setCurrentUser(user);
    return user;
  };

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Mettre à jour l'état local immédiatement
    setCurrentUser(user);
    
    return userCredential;
  };

  const logout = async () => {
    // Nettoyer le localStorage avant la déconnexion
    if (typeof window !== 'undefined') {
      localStorage.removeItem('bioconnect_selected_greenhouse');
    }
    await signOut(auth);
    setSelectedGreenhouse(null);
  };

  // Sélectionner une serre
  const selectGreenhouse = (greenhouseId) => {
    setSelectedGreenhouse(greenhouseId);
    // Sauvegarder dans localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('bioconnect_selected_greenhouse', greenhouseId);
    }
  };

  // Valeur du contexte qui sera fournie aux composants enfants
  const value = {
    currentUser,
    loading,
    userGreenhouses,
    selectedGreenhouse,
    selectGreenhouse,
    signup,
    login,
    logout,
    hasPushToken,
    setPushTokenPresent,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div style={{ textAlign: "center", marginTop: 100 }}>Chargement...</div>}
    </AuthContext.Provider>
  );
};